import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link2 } from "lucide-react";
import { timeAgo } from "./ReplicaBanner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
const TONE = { pending: "text-amber-200", approved: "text-sky-200", linked: "text-emerald-200", denied: "text-rose-200", expired: "text-white/40", cancelled: "text-white/40", superseded: "text-white/40" };
const CHANNEL_TONE = { dev: "border-sky-400/30 text-sky-200", stage: "border-amber-400/30 text-amber-100", prod: "border-rose-400/30 text-rose-200" };

/** Hub: link requests filed by spokes — approve (register / rotate) or deny. */
export function LinkRequestsPanel({ requests, canWrite, onChanged }) {
    const [busy, setBusy] = useState(null);
    const decide = async (r, decision) => {
        const what = decision === "approve"
            ? `APPROVE linking ${r.name} (${r.url}) to channel ${r.channel}?${r.existing_env_id ? ` An environment with this name already exists — its key is rotated and its URL/channel updated.` : " The environment is registered and its key minted."} The spoke picks the key up within 30 s.`
            : `Deny the link request of ${r.name}?`;
        const note = window.prompt(`${what}\n\nOptional note (audited, shown to the spoke):`, "");
        if (note === null) return;
        setBusy(r.link_id);
        try {
            await api.post(`/admin/sync/link-requests/${r.link_id}/${decision}`, { note: note || null });
            toast.success(decision === "approve" ? "Link approved — the spoke claims its key on its next poll" : "Link request denied");
            onChanged?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setBusy(null);
        }
    };
    const pending = (requests || []).filter((r) => r.status === "pending");
    const past = (requests || []).filter((r) => r.status !== "pending");
    return (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-6" data-testid="link-requests-panel">
            <div className="flex items-center gap-2 mb-1">
                <Link2 className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.5} />
                <div className="font-display text-lg tracking-tight">Link requests</div>
                {pending.length > 0 && <span className="ml-2 rounded bg-amber-400/10 border border-amber-400/30 px-2 py-0.5 text-[11px] text-amber-100" data-testid="link-pending-count">{pending.length} pending</span>}
            </div>
            <p className="text-xs text-white/50 mb-4">Spokes register themselves from their own Radar Sync page, choosing the channel they belong to. Approving registers the environment (or rotates the key of an existing name) and hands the key to the spoke automatically — it is never shown here. Requests expire after 72 h.</p>
            <ul className="space-y-2" data-testid="link-pending-list">
                {pending.map((r) => (
                    <li key={r.link_id} className="rounded border border-amber-400/30 bg-amber-400/5 p-3 text-xs flex flex-wrap items-center gap-3" data-testid={`link-row-${r.link_id}`}>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-white/90" data-testid={`link-name-${r.link_id}`}>{r.name}</span>
                                <span className={`rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${CHANNEL_TONE[r.channel] || "border-white/10 text-white/60"}`} data-testid={`link-channel-${r.link_id}`}>{r.channel}</span>
                                {r.mode && <span className="rounded border border-white/10 px-1.5 py-0.5 text-[10px] text-white/50">{r.mode}</span>}
                                {r.existing_env_id && <span className="rounded border border-sky-400/30 px-1.5 py-0.5 text-[10px] text-sky-200" data-testid={`link-existing-${r.link_id}`}>re-link · rotates key</span>}
                            </div>
                            <div className="text-white/60 font-mono text-[11px] mt-0.5 break-all">{r.url}</div>
                            <div className="text-white/50 mt-0.5">{r.requested_by ? `by ${r.requested_by} · ` : ""}{timeAgo(r.requested_at)} · expires {fmt(r.expires_at)}</div>
                            <div className="font-mono text-white/30">{r.link_id}</div>
                        </div>
                        {canWrite && (
                            <div className="ml-auto flex items-center gap-2">
                                <button disabled={busy === r.link_id} onClick={() => decide(r, "deny")} className="rounded border border-white/10 px-3 py-1.5 text-white/70 hover:bg-white/5 disabled:opacity-40" data-testid={`link-deny-${r.link_id}`}>Deny</button>
                                <button disabled={busy === r.link_id} onClick={() => decide(r, "approve")} className="rounded bg-white text-black px-3 py-1.5 font-medium hover:bg-white/90 disabled:opacity-40" data-testid={`link-approve-${r.link_id}`}>Approve</button>
                            </div>
                        )}
                    </li>
                ))}
                {!pending.length && <li className="text-xs text-white/40" data-testid="link-none">No pending link requests. Spokes file them from Settings → Radar Sync → Link to hub.</li>}
            </ul>
            {!!past.length && (
                <div className="mt-4">
                    <div className="label-xs mb-2">Trail</div>
                    <ul className="text-xs space-y-1" data-testid="link-trail">
                        {past.slice(0, 8).map((r) => (
                            <li key={r.link_id} className="flex flex-wrap items-center gap-2 border-t border-white/5 py-1.5" data-testid={`link-trail-${r.link_id}`}>
                                <span className={`font-medium ${TONE[r.status] || ""}`}>{r.status}</span>
                                <span className="text-white/80">{r.name}</span>
                                <span className="text-white/50">{r.channel}</span>
                                {r.env_id && <span className="font-mono text-white/40">{r.env_id}</span>}
                                <span className="ml-auto text-white/40">{r.decided_by ? `${r.decided_by} · ` : ""}{fmt(r.claimed_at || r.decided_at || r.updated_at)}</span>
                                {r.note && <span className="w-full text-white/40 italic">{r.note}</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
